import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useEffect, useMemo } from 'react';
import type { EmployeeOption, Option } from '../tasks.types';

/**
 * Department, then Employee — both searchable. Choosing a department narrows the employee
 * list to its members (none chosen = everyone), and a department change that leaves the
 * chosen employee outside it clears the employee.
 */
export default function DepartmentEmployeeFields({
  departments,
  employees,
  departmentId,
  onDepartmentChange,
  employeeId,
  onEmployeeChange,
  employeeLabel = 'Employee',
  employeeRequired,
  loading,
}: {
  departments: Option[];
  employees: EmployeeOption[];
  departmentId: number | null;
  onDepartmentChange: (id: number | null) => void;
  employeeId: number | null;
  onEmployeeChange: (id: number | null) => void;
  employeeLabel?: string;
  employeeRequired?: boolean;
  loading?: boolean;
}) {
  const inDepartment = useMemo(
    () => employees.filter((e) => departmentId === null || e.departmentId === departmentId),
    [employees, departmentId]
  );

  useEffect(() => {
    if (employeeId !== null && !inDepartment.some((e) => e.id === employeeId)) onEmployeeChange(null);
  }, [inDepartment, employeeId, onEmployeeChange]);

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ width: '100%' }}>
      <Autocomplete
        fullWidth
        size='small'
        options={departments}
        value={departments.find((d) => d.id === departmentId) ?? null}
        onChange={(_, d) => onDepartmentChange(d?.id ?? null)}
        getOptionLabel={(d) => d.label}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        openOnFocus
        autoHighlight
        loading={loading}
        noOptionsText='No matching department'
        renderInput={(p) => <TextField {...p} label='Department' placeholder='All departments — type to search' />}
      />
      <Autocomplete
        fullWidth
        size='small'
        options={inDepartment}
        value={inDepartment.find((e) => e.id === employeeId) ?? null}
        onChange={(_, e) => onEmployeeChange(e?.id ?? null)}
        getOptionLabel={(e) => `${e.name}${e.departmentName ? ` — ${e.departmentName}` : ''}`}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        openOnFocus
        autoHighlight
        loading={loading}
        noOptionsText={departmentId !== null ? 'No one in this department matches' : 'No matching employee'}
        renderInput={(p) => <TextField {...p} label={employeeLabel} required={employeeRequired} placeholder='Type to search' />}
      />
    </Stack>
  );
}
